import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState('');

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          api.setToken(token);
          const { user } = await api.getCurrentUser();
          setCurrentUser({
            uid: user.id,
            email: user.email,
            emailVerified: user.emailVerified,
            ...user
          });
        }
      } catch (error) {
        console.error('Error loading user:', error);
        // Invalid token, clear it
        api.logout();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Sign up
  async function signup(email, password, username) {
    try {
      const { user } = await api.register(email, password, username);
      setCurrentUser({
        uid: user.id,
        email: user.email,
        emailVerified: user.emailVerified,
        ...user
      });
      return { user };
    } catch (error) {
      throw error;
    }
  }

  // Log in
  async function login(email, password) {
    try {
      const { user } = await api.login(email, password);
      setCurrentUser({
        uid: user.id,
        email: user.email,
        emailVerified: user.emailVerified,
        ...user
      });
      return { user };
    } catch (error) {
      throw error;
    }
  }

  // Log out
  async function logout() {
    try {
      api.logout();
      setCurrentUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  // Send verification email
  async function sendVerificationEmail() {
    try {
      await api.verifyEmail();
    } catch (error) {
      throw error;
    }
  }

  // Update email
  async function updateEmail(email) {
    throw new Error('Email update not implemented yet');
  }

  // Update password
  async function updatePassword(password) {
    throw new Error('Password update not implemented yet');
  }

  const value = {
    currentUser,
    signup,
    login,
    logout,
    sendVerificationEmail,
    updateEmail,
    updatePassword,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}